import { MatDialog } from '@angular/material/dialog';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';
import { CommonPopup } from '../common-popup/common-popup';
import { CompanysitesService } from './company-sites.service';
import { SYSTEM_TYPES } from '../../services/kunde-kort.service';

const ACTIVATE_SERVICE = 'Activate service mode';
const DEACTIVATE_SERVICE = 'Deactivate service mode';

// Service mode is only in effect while the limit is in the future. The flag
// itself is cleared by the expireServiceMode cloud function, but that runs on
// a schedule, so the UI must not trust the flag alone.
export function isServiceModeActive(system: any): boolean {
  if (!system || system.inServiceMode !== true) return false;
  return !system.serviceModeLimit || system.serviceModeLimit > moment().unix();
}

// The one "Config" popup for a system. Opened from both the Systems tab card
// and the site detail page, which used to carry their own drifted copies.
export async function openSystemConfigPopup(dialog: MatDialog, siteService: CompanysitesService, siteId: string, systemId: string) {
  const result = await siteService.getSystemRefById(siteId, systemId);
  const obj = result.data();
  if (!obj) return;
  // The site-side copy lives under the system's own company/site, which is not
  // necessarily the one selected in the admin (e.g. opened from a kundekort).
  siteId = siteId || obj['siteId'];
  const companyId: string = obj['companyId'] || undefined;

  const inService = isServiceModeActive(obj);

  const dialogRef = dialog.open(CommonPopup, {
    disableClose: true,
    width: '500px',
    data: {
      text: "",
      formData: {
        title: `Update system ${obj['name']}` + (!inService ? ''
          : obj['serviceModeLimit'] ? ` (service mode until ${moment.unix(obj['serviceModeLimit']).format('HH:mm')})`
          : ' (service mode, no time limit)'),
        inputFeilds: [
          {
            labelName: "System ID",
            name: "systemId",
            required: true,
            value: obj['systemId'],
            type: "text",
            disabled: true,
            specialText: ""
          },
          {
            labelName: "Nummer",
            name: "number",
            required: true,
            value: obj['number'],
            type: "number",
            disabled: true,
            specialText: ""
          },
          {
            labelName: "System Name",
            name: "name",
            required: true,
            value: obj['name'],
            type: "text",
            specialText: ""
          },
          {
            labelName: "Description",
            name: "description",
            required: false,
            value: obj['description'],
            type: "text",
            specialText: ""
          },
          {
            labelName: "Systemtype",
            name: "systemType",
            required: false,
            type: null,
            value: obj['systemType'] || '',
            selectOptions: [{ value: '', label: '— Ingen —' }, ...SYSTEM_TYPES.map(t => ({ value: t, label: t }))],
            specialText: ""
          },
          {
            labelName: "IP Address",
            name: "ipAddress",
            required: false,
            value: obj['ipAddress'],
            type: "text",
            specialText: ""
          }
          ,
          {
            labelName: "IP",
            name: "ip",
            required: false,
            value: obj['ip']? obj['ip'] : '',
            type: "text",
            specialText: ""
          },
          {
            labelName: "Need Login",
            name: "needLogin",
            required: true,
            type: null,
            value: obj['needLogin'],
            specialText: "",
            selectOptions: [
              {
                label: "Yes",
                value: true
              },
              {
                label: "No",
                value: false
              }
            ]
          }, {
            labelName: "Credit Active",
            name: "isCreditActive",
            required: true,
            type: null,
            value: obj['isCreditActive'],
            specialText: "",
            selectOptions: [
              {
                label: "Yes",
                value: true
              },
              {
                label: "No",
                value: false
              }
            ]
          },
          {
            labelName: "Group Subsidy",
            name: "isGroupSubsidyActive",
            required: true,
            type: null,
            value: obj['isGroupSubsidyActive'],
            specialText: "",
            selectOptions: [
              {
                label: "Yes",
                value: true
              },
              {
                label: "No",
                value: false
              }
            ]
          },
  
          {
            labelName: "Garantiomsetning",
            name: "garantiomsetning",
            required: false,
            value: obj['garantiomsetning'],
            type: "number",
            specialText: ""
          },
          {
            labelName: "Customer reference (Deres ref.)",
            name: "customerReference",
            required: false,
            value: obj['customerReference'],
            type: "text",
            specialText: "Overrides the customer's default reference on invoices for THIS system's billing (garanti/driftskostnader/subsidy from this system). Leave blank to use the customer's own default."
          },
          {
            labelName: "Invoice by system",
            name: "invoiceBySystem",
            required: false,
            type: null,
            value: obj['invoiceBySystem'] === true,
            specialText: "Yes: every purchase at this system is invoiced to the system's own customer (Kundekort → Systems), whichever group the buyer belongs to. The system must be linked to exactly one customer. Applies when the month's reconciliation is run.",
            selectOptions: [
              { label: "Yes", value: true },
              { label: "No", value: false }
            ]
          },
          {
            labelName: "Overall Subsidy",
            name: "subsidy",
            required: true,
            value: obj['subsidy'],
            type: "number",
            specialText: ""
          }, {
            labelName: "MVA",
            name: "isMVAActive",
            required: true,
            type: null,
            value: obj['isMVAActive'],
            specialText: "",
            selectOptions: [
              {
                label: "Yes",
                value: true
              },
              {
                label: "No",
                value: false
              }
            ]
          }, {
            labelName: "Vipps type",
            name: "vippsType",
            required: true,
            type: null,
            value: obj['vippsType'],
            specialText: "",
            selectOptions: [
              {
                label: "Real",
                value: "REAL"
              },
              {
                label: "Demo",
                value: "DEMO"
              }
            ]
          },
          {
            labelName: "Pay with Vipps",
            name: "isVippsActive",
            required: true,
            type: null,
            value: obj['isVippsActive'],
            specialText: "",
            selectOptions: [
              {
                label: "Yes",
                value: true
              },
              {
                label: "No",
                value: false
              }
            ]
          }, {
            labelName: "Pay with Card",
            name: "isCardActive",
            required: true,
            type: null,
            value: obj['isCardActive'],
            specialText: "",
            selectOptions: [
              {
                label: "Yes",
                value: true
              },
              {
                label: "No",
                value: false
              }
            ]
          }, {
            labelName: "Guest Mode",
            name: "isGuestModeActive",
            required: true,
            type: null,
            value: obj['isGuestModeActive'],
            specialText: "",
            selectOptions: [
              {
                label: "Yes",
                value: true
              },
              {
                label: "No",
                value: false
              }
            ]
          }, {
            labelName: "Min. Temp",
            name: "minThresholdTemp",
            required: true,
            type: "number",
            value: obj['minThresholdTemp'],
            specialText: "",
          },
          {
            labelName: "Max. Temp",
            name: "maxThresholdTemp",
            required: true,
            type: "number",
            value: obj['maxThresholdTemp'],
            specialText: "",
          }, {
            labelName: "Halal Logo Active",
            name: "isHalalLogoActive",
            required: true,
            type: null,
            value: obj['isHalalLogoActive'],
            specialText: "",
            selectOptions: [
              {
                label: "Yes",
                value: true
              },
              {
                label: "No",
                value: false
              }
            ]
          }, {
            labelName: "New Logo Active",
            name: "isNewLogoActive",
            required: true,
            type: null,
            value: obj['isNewLogoActive'],
            specialText: "",
            selectOptions: [
              {
                label: "Yes",
                value: true
              },
              {
                label: "No",
                value: false
              }
            ]
          }, {
            labelName: "Vegetar Logo Active",
            name: "isVegeterianLogoActive",
            required: true,
            type: null,
            value: obj['isVegeterianLogoActive'],
            specialText: "",
            selectOptions: [
              {
                label: "Yes",
                value: true
              },
              {
                label: "No",
                value: false
              }
            ]
          }, {
            labelName: "Active Kantine",
            name: "kantineAvailble",
            required: true,
            type: null,
            value: obj['kantineAvailble'],
            specialText: "",
            selectOptions: [
              {
                label: "Yes",
                value: true
              },
              {
                label: "No",
                value: false
              }
            ]
          }, {
            labelName: "CheckStatus",
            name: "checkStatus",
            required: true,
            type: null,
            value: obj['checkStatus'],
            specialText: "",
            selectOptions: [
              {
                label: "Yes",
                value: true
              },
              {
                label: "No",
                value: false
              }
            ]
          }, {
            labelName: "Unavailable Message",
            name: "unavailableMessage",
            required: false,
            value: obj['unavailableMessage'],
            type: "text",
            specialText: ""
          }
          , {
            labelName: "WindowMessage",
            name: "windowMessage",
            required: false,
            value: obj['windowMessage'],
            type: "text",
            specialText: ""
          }, {
            labelName: "Popup Type",
            name: "popupType",
            required: true,
            type: null,
            value: obj['popupType'] || 'warning',
            specialText: "",
            selectOptions: [
              {
                label: "Warning",
                value: "warning"
              },
              {
                label: "Info",
                value: "info"
              },
              {
                label: "Alert",
                value: "alert"
              }
            ]
          }, {
            labelName: "Window Color",
            name: "windowColor",
            required: false,
            value: obj['windowColor'] || '#f59e0b',
            type: "color",
            specialText: ""
          }
  
        ],
        buttons: [
          {
            name: inService ? DEACTIVATE_SERVICE : ACTIVATE_SERVICE,
            type: "button",
            color: inService ? "primary" : "danger",
            validate: false
          },
          {
            name: "Save",
            type: "submit",
            color: "primary",
            validate: true
          }
        ]
      }
    }
  });

  dialogRef.afterClosed().subscribe(result => {
    if (!result) return;

    if (result === DEACTIVATE_SERVICE) {
      siteService.onUpdateSystem(siteId, systemId, { inServiceMode: false, serviceModeLimit: null }, companyId);
      return;
    }

    if (result === ACTIVATE_SERVICE) {
      openServiceModePopup(dialog, siteService, siteId, systemId, companyId);
      return;
    }

    if (result.value) siteService.onUpdateSystem(siteId, systemId, result.value, companyId);
  });
}

function openServiceModePopup(dialog: MatDialog, siteService: CompanysitesService, siteId: string, systemId: string, companyId?: string) {
  const dialogRef = dialog.open(CommonPopup, {
    disableClose: true,
    width: '500px',
    data: {
      text: "",
      formData: {
        title: `Activate service mode`,
        inputFeilds: [
          {
            labelName: "Duration",
            name: "minutes",
            required: true,
            type: null,
            specialText: "Service mode switches off automatically when the time runs out.",
            selectOptions: [
              { label: "30 minutes", value: 30 },
              { label: "60 minutes", value: 60 }
            ]
          }
        ],
        buttons: [
          {
            name: "Save",
            type: "submit",
            color: "primary",
            validate: true
          }
        ]
      }
    }
  });

  dialogRef.afterClosed().subscribe(result => {
    if (!result?.value) return;
    // Counted from Save, not from when the popup was opened.
    siteService.onUpdateSystem(siteId, systemId, {
      inServiceMode: true,
      serviceModeLimit: moment().add(Number(result.value['minutes']), 'minutes').unix()
    }, companyId);
  });
}
